import { Button } from '../../../../../../widgets/buttons';
import { Icon } from '../../../../../../widgets/icon';
import { SwitchGroup } from '../../../../../../widgets/switchGroup';
import React from 'react';
import { T } from 'src/shared/helpers';
import MicrophoneIcon from 'src/shared/icons/microphone-solid.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';

interface PromptAiAreaProps {
    disabled?: boolean;
    currentPromptValue?: string;
    onMicrophoneNoValue?: () => void;
    onMicrophoneError?: (error: any) => void;
    onTypePrompSwitch?: (text: boolean) => void;
    onValueChange?: (value: string) => void;
}

const PromptAiAreaContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 25px 25px 40px;
    background-color: ${theme.app.backgroundColor};
    border: 1px solid ${theme.app.borderColor};
    border-radius: 8px;
`;

const PromptAiAreaDisabled = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    background-color: ${theme.app.backgroundColor};
    display: flex;
    justify-content: center;
    align-items: center;
`;

const PromptAiAreaText = styled.textarea<{ $hasBorder: boolean }>`
    width: 100%;
    min-height: 200px;
    border: none;
    border-radius: 4px;
    padding: 10px;
    font-size: 14px;
    outline: none;
    resize: vertical;
    border: ${({ $hasBorder }) => ($hasBorder ? '1px solid ' + theme.app.borderColor : 'none')};
    background-color: ${theme.app.backgroundColor};

    &:disabled {
        background-color: ${theme.app.backgroundColor};
        cursor: not-allowed;
    }
`;

const StyledAlternativeCheckbox = styled(SwitchGroup)`
    align-self: flex-start;
    position: absolute;
    left: 15px;
    bottom: 10px;
`;

const VoiceSwitch = styled(Button)`
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 70%;
    right: 45%;
`;

const VoiceRecording = styled.div`
    width: 40px;
    position: absolute;
    top: 25%;
    left: 46%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${theme.app.secondary};

    & svg {
        width: 32px;
        height: 32px;
        fill: currentColor;
    }
`;

const Placeholder = styled.div`
    position: absolute;
    top: 25%;
    left: 30%;
    color: ${theme.app.textColor};
    pointer-events: none;
`;

export function PromptAiArea(props: PromptAiAreaProps) {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    const [isTextPrompt, setIsTextPrompt] = React.useState(false);
    const [value, setValue] = React.useState(props.currentPromptValue ?? '');
    const [voiceRecording, setVoiceRecording] = React.useState(false);
    const [recognition, setRecognition] = React.useState<any>(null);

    const startVoiceRecording = () => {
        if (props.disabled) return;
        setValue('');
        props.onTypePrompSwitch?.(false);

        try {
            const recognition = new SpeechRecognition();
            setRecognition(recognition);

            recognition.continuous = false;
            recognition.lang = navigator.language ?? 'en-US';
            recognition.interimResults = false;
            recognition.maxAlternatives = 1;

            recognition.start();

            recognition.onresult = (event: any) => {
                if (voiceRecording === false) return;
                const text = event.results[0][0].transcript;
                if (!text || text === '') {
                    props.onMicrophoneNoValue?.();
                    return;
                }
                setValue(text);
                console.log(`Confidence: ${event.results[0][0].confidence}`);

                setVoiceRecording(false);
            };
            recognition.onaudioend = (event: any) => {
                setVoiceRecording(false);
                if (value === '' || !value) props.onMicrophoneNoValue?.();
            };

            recognition.onspeechend = (event: any) => {
                recognition.stop();
                setVoiceRecording(false);
            };

            recognition.onnomatch = (event: any) => {
                setVoiceRecording(false);
                if (value === '' || !value) props.onMicrophoneNoValue?.();
            };

            recognition.onerror = (event: any) => {
                setVoiceRecording(false);
                if (event.error !== 'no-speech') props.onMicrophoneError?.(event.error);
            };
        } catch (e) {
            console.error(e);
            props.onMicrophoneError?.(e);
        }
    };

    return (
        <PromptAiAreaContainer>
            <StyledAlternativeCheckbox
                checked={isTextPrompt}
                disabled={props.disabled}
                label={T._('Keyboard mode', 'Admin')}
                // grayOnFalse={true}
                onChange={(tValue) => {
                    setIsTextPrompt(tValue);
                    props.onTypePrompSwitch?.(tValue);
                    setVoiceRecording(false);
                    if (!tValue) setValue('');
                }}
            />
            <PromptAiAreaText
                value={value}
                disabled={props.disabled || !isTextPrompt}
                $hasBorder={!(props.disabled || !isTextPrompt)}
                onChange={(e) => {
                    props.onValueChange?.(e.target.value);
                    setValue(e.target.value);
                }}
            />

            {!isTextPrompt && (
                <React.Fragment>
                    <VoiceSwitch
                        $size='small'
                        onClick={() => {
                            const value = !voiceRecording;
                            setVoiceRecording(value);
                            if (value) startVoiceRecording();
                            else {
                                recognition?.stop();
                                setVoiceRecording(false);
                            }
                        }}
                    >
                        <Icon style={{ width: '24px', height: '24px' }}>
                            <MicrophoneIcon style={{ width: '24px', height: '24px' }} />
                        </Icon>
                    </VoiceSwitch>

                    {voiceRecording && (
                        <VoiceRecording>
                            <svg xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 -960 960 960' width='24px' fill='#e3e3e3'>
                                <path d='M280-240v-480h80v480h-80ZM440-80v-800h80v800h-80ZM120-400v-160h80v160h-80Zm480 160v-480h80v480h-80Zm160-160v-160h80v160h-80Z' />
                            </svg>
                        </VoiceRecording>
                    )}
                </React.Fragment>
            )}
            {(!value || value === '') && (isTextPrompt || (!isTextPrompt && !voiceRecording)) && (
                <Placeholder>{isTextPrompt ? T._('Start typing here...', 'Admin') : T._('Press the microphone and begin speaking...', 'Admin')}</Placeholder>
            )}
        </PromptAiAreaContainer>
    );
}
